import { useEffect, useState } from "react";
import {
    AlertColor,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid2 as Grid,
    Typography,
} from "@mui/material";
import HTMLInput from "./HTMLInput";
import adminService from "../../../../services/admin.service";
import LoadingOverlay from "../../../Shared/LoadingOverlay";
import { FormSelect, FormSnackbar, FormTextField } from "../../../Shared/Form";
import { Template } from "../../../../types/template";

type CreateTemplateType = {
    fetchData: () => void;
    template?: Template;
    open: boolean;
    setOpen: (v: boolean) => void;
}

const CreateTemplate = ({ fetchData, template, open, setOpen }: CreateTemplateType) => {
    const [isLoading, setIsLoading] = useState(false);
    const [createDisabled, setCreateDisabled] = useState(true);

    const [snackbar, setSnackbar] = useState("");
    const [severity, setSeverity] = useState<AlertColor>("success");

    // Form Values
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [code, setCode] = useState("");
    const [orientation, setOrientation] = useState("");
    const [contentType, setContentType] = useState("");
    const [transitionType, setTransitionType] = useState("");

    const reset = () => {
        setName("");
        setDescription("");
        setCode("");
        setOrientation("");
        setContentType("");
        setTransitionType("");
    }

    useEffect(() => {
        if (template) {
            setName(template.name);
            setDescription(template.description);
            setCode(template.html);
            setOrientation(template.orientation);
            setContentType(template.contentType);
            setTransitionType(template.transitionType);
        } else {
            reset();
        }
    }, [template])

    // Error Handling
    const [nameError, setNameError] = useState("");
    const [descriptionError, setDescriptionError] = useState("");

    useEffect(() => {
        if (name.length > 50) {
            setNameError("Title cannot be more than 50 characters");
        } else {
            setNameError("");
        }
    }, [name])

    useEffect(() => {
        if (description.length > 150) {
            setDescriptionError("Description cannot be more than 150 characters");
        } else {
            setDescriptionError("");
        }
    }, [description])

    useEffect(() => {
        if (name && orientation && contentType && transitionType && !nameError && !descriptionError) {
            setCreateDisabled(false);
        } else {
            setCreateDisabled(true);
        }
    }, [nameError, descriptionError, name, orientation, contentType, transitionType])

    // Ad Create Handler
    async function handleCreateAd() {
        setIsLoading(true);
        const response = await adminService.createTemplate(
            name,
            description,
            code,
            orientation,
            contentType,
            transitionType
        )
        if (response) {
            setSeverity("success");
            setSnackbar("Successfully created template.");
            setOpen(false);
            reset();
            fetchData();
        } else {
            setSeverity("error");
            setSnackbar("There was an error creating the template.");
        }
        setIsLoading(false);
    }

    async function handleUpdateAd() {
        if (template) {
            setIsLoading(true);
            const response = await adminService.updateTemplate(
                template.id,
                name,
                description,
                code,
                orientation,
                contentType,
                transitionType
            )
            if (response) {
                setSeverity("success");
                setSnackbar("Successfully updated template.");
                setOpen(false);
                reset();
                fetchData();
            } else {
                setSeverity("error");
                setSnackbar("There was an error updating the template.");
            }
            setIsLoading(false);
        }
    }

    return (
        <>
            <Dialog
                open={open}
                onClose={() => { setOpen(false) }}
                fullWidth={true}
                maxWidth="lg"
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <LoadingOverlay open={isLoading} />
                <DialogTitle id="alert-dialog-title">Create New Template</DialogTitle>
                <DialogContent>
                    <Typography variant="h6" pt={1} pb={2}>Template Info</Typography>
                    <Grid container spacing={3} width={"100%"}>
                        <FormTextField
                            label="Template Title"
                            value={name}
                            setValue={setName}
                            error={nameError}
                            size={6}
                        />
                        <FormSelect
                            id = "orientation-label"
                            label = "Orientation"
                            value = {orientation}
                            setValue = {setOrientation}
                            items = {["Landscape", "Portrait"]}
                            itemValues = {["landscape", "portrait"]}
                            disabled={isLoading}
                            resettable={false}
                            size={2}
                        />
                        <FormSelect
                            id = "content-label"
                            label = "Content Type"
                            value = {contentType}
                            setValue = {setContentType}
                            items = {["Any Media", "Image Only", "Video Only"]}
                            itemValues={["mix", "image", "video"]}
                            disabled={isLoading}
                            resettable={false}
                            size={2}
                        />
                        <FormSelect
                            id = "transition-label"
                            label = "Transition Type"
                            value = {transitionType}
                            setValue = {setTransitionType}
                            items = {["Slide", "Zoom", "Fade", "None"]}
                            disabled={isLoading}
                            resettable={false}
                            size={2}
                        />
                    </Grid>
                    <Grid container width={"100%"} pt={2}>
                        <FormTextField
                            label="Description"
                            value={description}
                            setValue={setDescription}
                            helperText="Provide some details about the template"
                            error={descriptionError}
                            size={12}
                        />
                    </Grid>
                    <Divider sx={{ mt: 3, mb: 3 }} />
                    <HTMLInput code={code} setCode={setCode} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setOpen(false) }} disabled={isLoading}>
                        Close
                    </Button>
                    <Button
                        onClick={template ? handleUpdateAd : handleCreateAd}
                        disabled={createDisabled || isLoading}
                    >
                        {template ? "Update" : "Create"} Template
                    </Button>
                </DialogActions>
            </Dialog>
            <FormSnackbar message={snackbar} setMessage={setSnackbar} severity={severity} />
        </>
    )
}

export default CreateTemplate;
